import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    confirmProductImportBatch,
    getProductImportBatch,
    listProductImportRows,
    ProductImportBatch,
    ProductImportRow,
    ProductImportRowMessage,
    ProductImportRowStatus,
} from '../../services/productImport.service';
import { showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { webLoadingFalse, webLoadingTrue, webNavigate } from '../../services/navigate.service';
import { useResponsive } from '../../hook/useResponsive';
import { extractValues, updateUrlParams } from '../../validation/ValidationInput';
import {
    ListColumn,
    ListQuery,
    ListScreen,
    ListScreenHandle,
    MONO,
    PAGE_SIZES,
    t,
} from '../../components/list';
import {
    BatchSummary,
    Card,
    IconArrowLeft,
    IconFile,
    MessageList,
    STATUS_PALETTE,
    Stepper,
    StepperStage,
    TOKENS,
    formatExactDate,
    ghostButton,
    primaryButton,
    secondaryButton,
} from './productImportShared';

type StatusFilter = 'ALL' | ProductImportRowStatus;

const STATUS_TABS: { id: StatusFilter; label: string }[] = [
    { id: 'ALL', label: 'All rows' },
    { id: 'READY_CREATE', label: 'Ready' },
    { id: 'SKIP_EXISTING', label: 'Skip' },
    { id: 'NEEDS_ATTENTION', label: 'Needs attention' },
];

const ROW_STATUS: Record<ProductImportRowStatus, { label: string; bg: string; fg: string; dot: string }> = {
    READY_CREATE: { label: 'Ready', bg: STATUS_PALETTE.readyBg, fg: STATUS_PALETTE.readyText, dot: STATUS_PALETTE.readyDot },
    SKIP_EXISTING: { label: 'Skip', bg: STATUS_PALETTE.skipBg, fg: STATUS_PALETTE.skipText, dot: STATUS_PALETTE.skipDot },
    NEEDS_ATTENTION: { label: 'Needs attention', bg: STATUS_PALETTE.needsBg, fg: STATUS_PALETTE.needsText, dot: STATUS_PALETTE.needsDot },
};

const ELLIPSIS: React.CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

const LEVEL_RANK: Record<string, number> = { error: 0, warning: 1, info: 2 };

/** Same dot / text colours as the shared MessageList. */
const levelColors = (level: string) => {
    if (level === 'error') return { dot: STATUS_PALETTE.needsDot, text: STATUS_PALETTE.needsText };
    if (level === 'warning') return { dot: STATUS_PALETTE.pendingDot, text: STATUS_PALETTE.pendingText };
    return { dot: STATUS_PALETTE.infoDot, text: TOKENS.textMuted };
};

const itemNameOf = (r: ProductImportRow) => String(r.rawPayload?.item_name ?? '');
const itemTypeOf = (r: ProductImportRow) => String(r.rawPayload?.item_type ?? '');

const rowStatusLabel = (status: ProductImportRowStatus, language: any) => {
    const label = ROW_STATUS[status]?.label ?? String(status ?? '');
    return t(language, label, label);
};

/** The shared RowStatusChip look, with a translated label. */
const RowStatusPill: React.FC<{ status: ProductImportRowStatus; language: any }> = ({ status, language }) => {
    const s = ROW_STATUS[status] ?? ROW_STATUS.SKIP_EXISTING;
    return (
        <span
            className="font-inter"
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                maxWidth: '100%',
                background: s.bg,
                color: s.fg,
                padding: '2px 10px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                lineHeight: '18px',
                whiteSpace: 'nowrap',
            }}
        >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flex: 'none' }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{rowStatusLabel(status, language)}</span>
        </span>
    );
};

/** Desktop rows are one line: the most severe message, "+N" for the rest; a click opens them all. */
const MessageSummary: React.FC<{
    messages: ProductImportRowMessage[] | null | undefined;
    onOpen: () => void;
}> = ({ messages, onOpen }) => {
    if (!messages || messages.length === 0) return <span style={{ color: TOKENS.textDim }}>—</span>;
    const first = [...messages].sort((a, b) => (LEVEL_RANK[a.level] ?? 3) - (LEVEL_RANK[b.level] ?? 3))[0];
    const colors = levelColors(first.level);
    const more = messages.length - 1;
    return (
        <button
            type="button"
            onClick={onOpen}
            title={messages.map(m => `${m.field}: ${m.message}`).join('\n')}
            className="font-inter"
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                maxWidth: '100%',
                minWidth: 0,
                padding: 0,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
            }}
        >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: colors.dot, flex: 'none' }} />
            <span style={{ fontSize: 12.5, color: colors.text, minWidth: 0, ...ELLIPSIS }}>
                <strong style={{ fontWeight: 600 }}>{first.field}:</strong> {first.message}
            </span>
            {more > 0 && (
                <span
                    style={{
                        flex: 'none',
                        fontSize: 11,
                        fontWeight: 700,
                        color: TOKENS.tealMuted,
                        background: TOKENS.tealLight,
                        borderRadius: 999,
                        padding: '1px 8px',
                    }}
                >
                    +{more}
                </span>
            )}
        </button>
    );
};

/** Every message of one row, for the desktop table (the mobile record sheet lists them inline). */
const MessagesDialog: React.FC<{ row: ProductImportRow | null; language: any; onClose: () => void }> = ({
    row,
    language,
    onClose,
}) => {
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    useEffect(() => {
        if (!row) return undefined;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onCloseRef.current();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [row]);

    if (!row) return null;
    const context = [`${t(language, 'Row', 'Row')} ${row.rowNumber}`, row.sku, itemNameOf(row)]
        .filter(Boolean)
        .join(' · ');
    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 200,
                background: 'rgba(34,37,37,.32)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                boxSizing: 'border-box',
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label={t(language, 'Messages', 'Messages')}
                onClick={e => e.stopPropagation()}
                style={{
                    background: TOKENS.surface,
                    borderRadius: TOKENS.radiusLarge,
                    padding: 24,
                    width: 560,
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    boxSizing: 'border-box',
                    boxShadow: TOKENS.cardShadowElevated,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
                    <div style={{ minWidth: 0 }}>
                        <div className="font-inter" style={{ fontSize: 18, fontWeight: 600, color: TOKENS.text }}>
                            {t(language, 'Messages', 'Messages')}
                        </div>
                        <div className="font-inter" style={{ fontSize: 13, color: TOKENS.textMuted, marginTop: 2, overflowWrap: 'anywhere' }}>
                            {context}
                        </div>
                    </div>
                    <button type="button" onClick={onClose} style={{ ...ghostButton(), padding: '4px 8px', flex: 'none' }}>
                        {t(language, 'Close', 'Close')}
                    </button>
                </div>
                <MessageList messages={row.messages} />
            </div>
        </div>
    );
};

/** The status tab named in the URL (`?status=`), or All. */
const tabFromUrl = (): StatusFilter => {
    const params: any = extractValues(window.location.search.split('&'));
    return STATUS_TABS.some(tab => tab.id === params.status) ? params.status : 'ALL';
};

const ProductImportBatchDetail: React.FC = () => {
    const preLanguage = localStorage.getItem('language') || 'en';
    const path = window.location.pathname;
    const id: string = path.split('/').pop() || '';
    const { isMobile } = useResponsive();
    const listRef = useRef<ListScreenHandle>(null);
    const stepperBoxRef = useRef<HTMLDivElement>(null);

    const [language, setLanguage] = useState<any>(window.languageContext?.language ?? null);
    const [batch, setBatch] = useState<ProductImportBatch | null>(null);
    // Read before the list mounts so its key does not remount it on load.
    const [tab, setTab] = useState<StatusFilter>(tabFromUrl);
    const [loading, setLoading] = useState(true);
    const [isConfirming, setIsConfirming] = useState(false);
    const [messagesFor, setMessagesFor] = useState<ProductImportRow | null>(null);

    useEffect(() => {
        if (window.languageContext) setLanguage(window.languageContext.language);
    }, [window.languageContext]);

    const tr = (key: string) => t(language, key, key);

    const canConfirm = useMemo(
        () =>
            !!batch &&
            batch.status === 'PREVIEWED' &&
            batch.needsAttentionCount === 0,
        [batch],
    );

    // ── load batch summary (again on newToken) ───────────────────────────────
    useEffect(() => {
        if (!id) return undefined;
        let active = true;
        const loadBatch = async () => {
            try {
                webLoadingTrue();
                const b = await getProductImportBatch(id);
                if (active) setBatch(b);
            } catch (err: any) {
                showApiError(err, { language: window.languageContext?.language, titleFallback: 'Failed to load batch' });
            } finally {
                if (active) setLoading(false);
                webLoadingFalse();
            }
        };
        loadBatch();
        document.addEventListener('newToken', loadBatch);
        return () => {
            active = false;
            document.removeEventListener('newToken', loadBatch);
        };
    }, [id]);

    // Past preview the active step is the last one, so scroll the phone's stepper box to it.
    useEffect(() => {
        const box = stepperBoxRef.current;
        if (box && isMobile) box.scrollLeft = batch && batch.status !== 'PREVIEWED' ? box.scrollWidth : 0;
    }, [batch?.status, isMobile]);

    /** One page of rows for the active tab; the endpoint pages and filters by rowStatus only. */
    const fetchRows = async (query: ListQuery) => {
        const res = await listProductImportRows(id, query.page, query.limit, tab === 'ALL' ? undefined : tab);
        return { rows: res?.data ?? [], count: res?.total ?? 0 };
    };

    /** A tab change starts at page 1; the URL is pushed first so the remounted list restores from it. */
    const changeTab = (next: StatusFilter) => {
        if (next === tab) return;
        const params: any = extractValues(window.location.search.split('&'));
        const limit = PAGE_SIZES.includes(+params.limit) ? +params.limit : 50;
        // In place, like the list's own paging: the tab is read from the URL only on mount.
        window.history.replaceState(window.history.state, '', location.pathname + updateUrlParams({ page: 1, limit }, { status: next === 'ALL' ? '' : next }));
        setTab(next);
    };

    // ── confirm action ───────────────────────────────────────────────────────
    const onConfirm = async () => {
        if (!batch || !canConfirm) return;
        const ok = window.confirm(
            `Confirm ${batch.readyCount} new product(s)? This will create them immediately.`,
        );
        if (!ok) return;
        try {
            setIsConfirming(true);
            webLoadingTrue();
            const result = await confirmProductImportBatch(batch.id);
            setBatch(result);
            // Rows now carry their created product ids.
            listRef.current?.reload();
            if (result.status === 'CONFIRMED') {
                showToast({
                    type: 'success',
                    message: `Imported ${result.readyCount} product(s) successfully`,
                });
            } else if (result.status === 'FAILED') {
                showToast({
                    type: 'error',
                    message: `Confirm failed: ${result.errorDetail ?? 'unknown error'}`,
                });
            }
        } catch (err: any) {
            showApiError(err, { language: window.languageContext?.language, titleFallback: 'Confirm failed' });
        } finally {
            setIsConfirming(false);
            webLoadingFalse();
        }
    };

    // ── render ───────────────────────────────────────────────────────────────

    const statePadding = isMobile ? '16px 0' : 32;

    if (loading && !batch) {
        return (
            <div style={{ padding: statePadding, background: TOKENS.bg, height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
                <Card>
                    <div style={{ padding: 24, textAlign: 'center', color: TOKENS.textMuted }}>
                        {tr('Loading batch…')}
                    </div>
                </Card>
            </div>
        );
    }

    if (!batch) {
        return (
            <div style={{ padding: statePadding, background: TOKENS.bg, height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
                <Card>
                    <div style={{ padding: isMobile ? 8 : 32, textAlign: 'center' }}>
                        <div style={{ fontSize: 40, marginBottom: 8 }}>🔍</div>
                        <h3
                            style={{
                                color: TOKENS.tealDark,
                                fontWeight: 700,
                                margin: 0,
                            }}
                        >
                            {tr('Batch not found')}
                        </h3>
                        <p style={{ color: TOKENS.textMuted, marginTop: 8 }}>
                            {tr('This import batch may have been deleted or belongs to another company.')}
                        </p>
                        <button
                            type="button"
                            onClick={() => webNavigate(`/${preLanguage}/items/import/history`)}
                            style={{ ...primaryButton(), marginTop: 16 }}
                        >
                            {tr('Back to history')}
                        </button>
                    </div>
                </Card>
            </div>
        );
    }

    const stepperStage: StepperStage =
        batch.status === 'CONFIRMED' ||
        batch.status === 'CONFIRMING' ||
        batch.status === 'FAILED' ||
        batch.status === 'EXPIRED'
            ? 'confirm'
            : 'preview';
    const stepperFailed = batch.status === 'FAILED' || batch.status === 'EXPIRED';

    const columns: ListColumn<ProductImportRow>[] = [
        {
            key: 'rowNumber',
            label: 'Row',
            width: 64,
            minWidth: 56,
            hideable: false,
            // Mobile subtitle reads "Row 12 · SKU-001".
            value: (r, ctx) => `${t(ctx.language, 'Row', 'Row')} ${r.rowNumber}`,
            render: r => (
                <span className="font-inter" style={{ fontSize: 13, color: TOKENS.textDim, fontVariantNumeric: 'tabular-nums' }}>
                    {r.rowNumber}
                </span>
            ),
            mobile: 'subtitle',
        },
        {
            key: 'sku',
            label: 'SKU',
            width: 150,
            minWidth: 96,
            value: r => r.sku ?? '—',
            render: r => (
                <div className="font-inter" style={{ fontSize: 14, fontWeight: 600, color: TOKENS.text, ...ELLIPSIS }}>
                    {r.sku ?? '—'}
                </div>
            ),
            mobile: 'subtitle',
        },
        {
            key: 'itemName',
            label: 'Item name',
            width: 240,
            minWidth: 120,
            value: r => itemNameOf(r),
            mobileRender: (r, ctx) => itemNameOf(r) || r.sku || `${t(ctx.language, 'Row', 'Row')} ${r.rowNumber}`,
            mobile: 'title',
        },
        {
            key: 'itemType',
            label: 'Type',
            width: 120,
            minWidth: 80,
            value: r => itemTypeOf(r),
            render: r => (
                <div className="font-inter" style={{ fontSize: 13, color: TOKENS.textMuted, ...ELLIPSIS }}>
                    {itemTypeOf(r)}
                </div>
            ),
            mobile: 'sheet',
        },
        {
            key: 'rowStatus',
            label: 'Status',
            width: 150,
            minWidth: 110,
            value: (r, ctx) => rowStatusLabel(r.rowStatus, ctx.language),
            render: (r, ctx) => <RowStatusPill status={r.rowStatus} language={ctx.language} />,
            mobile: 'trailing',
        },
        {
            key: 'messages',
            label: 'Messages',
            width: 380,
            minWidth: 160,
            value: r => (r.messages ?? []).map(m => `${m.field}: ${m.message}`).join('; '),
            render: r => <MessageSummary messages={r.messages} onOpen={() => setMessagesFor(r)} />,
            // The record sheet wraps, so the phone shows every message in full.
            mobileRender: r => <MessageList messages={r.messages} />,
            mobile: 'sheet',
        },
    ];
    if (batch.status === 'CONFIRMED') {
        columns.push({
            key: 'createdProductId',
            label: 'Created product',
            width: 130,
            minWidth: 96,
            render: r =>
                r.createdProductId ? (
                    <span title={r.createdProductId} style={{ color: TOKENS.textMuted, fontSize: 12, fontFamily: MONO }}>
                        {r.createdProductId.slice(0, 8)}…
                    </span>
                ) : (
                    <span style={{ color: TOKENS.textDim }}>—</span>
                ),
            mobileRender: r =>
                r.createdProductId ? (
                    <span style={{ color: TOKENS.textMuted, fontSize: 12, fontFamily: MONO, wordBreak: 'break-all' }}>
                        {r.createdProductId}
                    </span>
                ) : (
                    <span style={{ color: TOKENS.textDim }}>—</span>
                ),
            mobile: 'sheet',
        });
    }

    const emptyState = {
        title: tab === 'ALL' ? tr('No rows in this batch') : tr('No rows to show for this filter.'),
        desc: tab === 'ALL' ? tr('This upload produced no rows.') : tr('Try another status tab.'),
        img: <IconFile size={28} color={TOKENS.teal} />,
    };

    return (
        <>
            <div
                style={{
                    padding: isMobile ? '16px 0 24px' : '32px 32px 48px',
                    background: TOKENS.bg,
                    // Own scroll context: the host shell clips overflow on its content area.
                    height: '100%',
                    overflowY: 'auto',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 20,
                }}
            >
                {/* Header */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: isMobile ? 16 : 24,
                        flexWrap: 'wrap',
                        flexShrink: 0,
                    }}
                >
                    <div style={{ minWidth: 0 }}>
                        <button
                            type="button"
                            onClick={() => webNavigate(`/${preLanguage}/items/import/history`)}
                            style={{
                                ...ghostButton(),
                                paddingLeft: 0,
                                marginBottom: 6,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                            }}
                        >
                            <IconArrowLeft size={14} color={TOKENS.teal} /> {tr('Back to history')}
                        </button>
                        <h1
                            style={{
                                margin: 0,
                                color: TOKENS.tealDark,
                                fontSize: isMobile ? 24 : 32,
                                fontWeight: 700,
                                letterSpacing: -0.6,
                                lineHeight: 1.15,
                            }}
                        >
                            {tr('Import batch')}
                        </h1>
                        <p style={{ margin: '8px 0 0', color: TOKENS.textMuted, fontSize: 14, maxWidth: 520 }}>
                            {tr('Uploaded')} {formatExactDate(batch.createdAt)}
                            {batch.confirmedAt && ` · ${tr('Confirmed')} ${formatExactDate(batch.confirmedAt)}`}
                        </p>
                    </div>
                    {canConfirm && (
                        <button
                            type="button"
                            onClick={onConfirm}
                            disabled={isConfirming}
                            style={{
                                ...primaryButton(isConfirming),
                                background: isConfirming ? '#A8C9CA' : TOKENS.tealGradient,
                            }}
                        >
                            {isConfirming
                                ? tr('Confirming…')
                                : `${tr('Confirm')} ${batch.readyCount} ${batch.readyCount === 1 ? tr('product') : tr('products')}`}
                        </button>
                    )}
                </div>

                {/* The stepper is ~440px wide: on a phone it scrolls in its own box, active step in view. */}
                <div
                    ref={stepperBoxRef}
                    style={
                        isMobile
                            ? { maxWidth: '100%', overflowX: 'auto', scrollbarWidth: 'none', padding: '2px 2px 10px', flexShrink: 0 }
                            : { flexShrink: 0 }
                    }
                >
                    <Stepper stage={stepperStage} failed={stepperFailed} />
                </div>

                {/* Error banner if FAILED */}
                {batch.status === 'FAILED' && batch.errorDetail && (
                    <Card
                        style={{
                            background: STATUS_PALETTE.needsBg,
                            borderColor: STATUS_PALETTE.needsDot,
                            flexShrink: 0,
                        }}
                    >
                        <div
                            style={{
                                color: STATUS_PALETTE.needsText,
                                fontWeight: 700,
                                fontSize: 14,
                                marginBottom: 4,
                            }}
                        >
                            {tr('Confirm failed')}
                        </div>
                        <div style={{ color: STATUS_PALETTE.needsText, fontSize: 13, overflowWrap: 'anywhere' }}>
                            {batch.errorDetail}
                        </div>
                    </Card>
                )}

                <div style={{ flexShrink: 0 }}>
                    <BatchSummary batch={batch} />
                </div>

                {/* Status tabs + rows; on desktop the table fills what is left, min 480px. */}
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        ...(isMobile ? { flexShrink: 0 } : { flex: '1 0 480px', minHeight: 0 }),
                    }}
                >
                    <div
                        role="tablist"
                        aria-label={tr('Row status')}
                        style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}
                    >
                        {STATUS_TABS.map(item => (
                            <button
                                key={item.id}
                                type="button"
                                role="tab"
                                aria-selected={tab === item.id}
                                onClick={() => changeTab(item.id)}
                                style={tabButton(tab === item.id)}
                            >
                                {tr(item.label)}
                                {item.id === 'NEEDS_ATTENTION' && batch.needsAttentionCount > 0 && (
                                    <span
                                        style={{
                                            marginLeft: 8,
                                            background: STATUS_PALETTE.needsBg,
                                            color: STATUS_PALETTE.needsText,
                                            padding: '1px 8px',
                                            borderRadius: 999,
                                            fontSize: 11,
                                            fontWeight: 700,
                                        }}
                                    >
                                        {batch.needsAttentionCount}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                    <ListScreen
                        key={`${id}:${tab}`}
                        ref={listRef}
                        language={language}
                        columns={columns}
                        fetchRows={fetchRows}
                        rowKey={(r: ProductImportRow) => String(r.id)}
                        search={false}
                        countNoun="rows"
                        emptyState={emptyState}
                        extraUrlParams={{ status: tab === 'ALL' ? '' : tab }}
                    />
                </div>

                {/* Footer actions */}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', flexShrink: 0 }}>
                    <button
                        type="button"
                        onClick={() => webNavigate(`/${preLanguage}/items/import`)}
                        style={secondaryButton()}
                    >
                        {tr('New import')}
                    </button>
                </div>
            </div>
            <MessagesDialog row={messagesFor} language={language} onClose={() => setMessagesFor(null)} />
        </>
    );
};

const tabButton = (active: boolean): React.CSSProperties => ({
    background: active ? TOKENS.teal : TOKENS.surface,
    color: active ? '#fff' : TOKENS.tealDark,
    border: `1px solid ${active ? TOKENS.teal : TOKENS.borderStrong}`,
    borderRadius: 999,
    padding: '8px 16px',
    minHeight: 36,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    transition: 'background 120ms ease, border-color 120ms ease',
});

export default ProductImportBatchDetail;
